import { Link } from '@tanstack/react-router'
import {
  BadgeCheck,
  Calendar,
  Images,
  LayoutDashboard,
  MapPin,
  Megaphone,
  MessagesSquare,
  Settings,
  Share2,
  Sparkles,
  Store,
  UtensilsCrossed,
  Wallet,
  X,
} from 'lucide-react'
import { type ComponentType, useMemo } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useOrganizationProfiles } from '~/entities/profile/model/use-profiles'
import { AdminBypassToggle } from '~/features/stripe/admin-bypass-banner'
import * as m from '~/paraglide/messages'
import { storageManager } from '~/shared/lib/storage-manager'
import { cn } from '~/shared/lib/utils'
import { type NavKey, navigationFor } from './lib/profile-navigation'
import { ProfileSwitcher } from './profile-switcher'
import { SidebarAccountCard } from './sidebar-account-card'

interface DashboardLeftSidebarProps {
  sidebarOpen: boolean
  onCloseMobile: () => void
}

export function DashboardLeftSidebar({ sidebarOpen, onCloseMobile }: DashboardLeftSidebarProps) {
  const isAdminSession = storageManager.isAdmin()
  const isAdminViewing = isAdminSession && !!storageManager.getAdminViewingBusinessId()
  const adminViewingBusiness = isAdminViewing ? storageManager.getAdminViewingBusinessData() : null

  const { data: business } = useCurrentBusiness()
  const { enabled, active } = useOrganizationProfiles(business?.id)
  const navigation = useMemo(() => navigationFor(enabled ? active : null), [enabled, active])

  return (
    <aside
      className={cn(
        'left-0 z-50 fixed inset-y-0 w-64 bg-card border-r transition-transform duration-300 ease-in-out pointer-events-auto lg:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      <div className="relative flex flex-col h-full overflow-y-auto">
        <div className="flex shrink-0 items-center justify-between gap-2 px-3 border-b h-16">
          <h1 className="flex items-center gap-2 whitespace-nowrap">
            <img src="/pulsify-wordmark.svg" alt="Pulsify" className="h-6 w-auto self-center" />
            <span className="font-medium text-muted-foreground text-sm tracking-wide">
              Business
            </span>
          </h1>
          <button type="button" onClick={onCloseMobile} className="lg:hidden">
            <X className="w-6 h-6" />
          </button>
        </div>

        {isAdminViewing && (
          <div className="bg-blue-50 dark:bg-blue-950 px-3 py-2 border-b border-blue-200 dark:border-blue-800 flex items-center justify-between gap-2">
            <p className="text-blue-700 dark:text-blue-300 text-xs font-medium truncate">
              {adminViewingBusiness?.legalName || m.adminViewMode()}
            </p>
            <AdminBypassToggle />
          </div>
        )}

        <ProfileSwitcher />

        <nav className="flex-auto shrink-0 space-y-1 px-3 py-4">
          {navigation.map((item) => {
            const { name, icon: Icon } = NAV_LABELS[item.key]()
            return (
              <Link
                key={item.key}
                to={item.to}
                params={item.params}
                search={item.search}
                activeOptions={{ exact: item.to === '/dashboard' }}
                activeProps={{
                  className: 'bg-brand-subtle text-brand-subtle-foreground',
                }}
                inactiveProps={{
                  className: 'text-sidebar-foreground hover:bg-accent hover:text-accent-foreground',
                }}
                className="flex items-center gap-3 px-3 py-2 squircle rounded-md font-medium text-sm transition-colors duration-150 ease-spring"
                onClick={onCloseMobile}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className="truncate">{name}</span>
              </Link>
            )
          })}
        </nav>

        <SidebarAccountCard isAdminViewing={isAdminViewing} onNavigate={onCloseMobile} />
      </div>
    </aside>
  )
}

const NAV_LABELS: Record<
  NavKey,
  () => { name: string; icon: ComponentType<{ className?: string }> }
> = {
  overview: () => ({ name: m.overview(), icon: LayoutDashboard }),
  listings: () => ({ name: m.listings(), icon: Store }),
  place: () => ({ name: m.profileNavPlace(), icon: MapPin }),
  events: () => ({ name: m.events(), icon: Calendar }),
  earnings: () => ({ name: m.earningsNav(), icon: Wallet }),
  groups: () => ({ name: m.groupsNav(), icon: MessagesSquare }),
  organizer: () => ({ name: m.organizerNav(), icon: BadgeCheck }),
  menu: () => ({ name: m.menuPageTitle(), icon: UtensilsCrossed }),
  actions: () => ({ name: m.actionsPageTitle(), icon: Megaphone }),
  aiStudio: () => ({ name: m.aiStudio(), icon: Sparkles }),
  media: () => ({ name: m.mediaLibrary(), icon: Images }),
  crossposting: () => ({ name: m.crossposting(), icon: Share2 }),
  settings: () => ({ name: m.settings(), icon: Settings }),
}
